import React, { Component } from "react";
import PropTypes from "prop-types";
import { withRouter } from "react-router";
import { Layout, Card, Select, Radio, Tabs, Divider, Alert, Row, Col } from "antd";
const { Sider, Content } = Layout;
const Option = Select.Option;
const TabPane = Tabs.TabPane;
import CallToLogin from "../../common/call-to-login";
import CallToUpgrade from "../../common/call-to-upgrade";
import ScoreHistogram from "./score-histogram";
import DateHistogram from "./date-histogram";
import FieldAggregation from "./field-agg";
import AssetList from "./asset-table";
import CitationAnalysis from "./citation-analysis";
import CountHeader from "./count-header";
import LandscapeToolbar from "./toolbar";
import AppliedFilters from "../../common/applied-filters";
import { keepOnlyLandscapeQueryStrings, mapLandscapeQueryStringToName } from "../../../helpers/url";
import { toSplittedNumbers } from "../../../helpers/converters";
import { omitBy, omit, isEmpty, pick, isNumber } from "lodash";
import { handleCountryFilter, countByValues, countBy2View } from "./utils";
import VennDiagram from "./venn";
import VennMultiSelect from "./venn-multi-select";

const styles = {
  body: {
    width: "100%",
    padding: 30
  },
  sider: {
    background: "transparent",
    marginRight: 16
  },
  verticalGutter: {
    marginTop: 16
  },
  verticalGutterSmall: {
    marginTop: 8
  },
  chartCard: {
    textAlign: "center",
    minHeight: 400
  }
};

class Landscape extends Component {
  static propTypes = {
    accessLevel: PropTypes.oneOf(["full", "trial", "guest", "public"]).isRequired,
    title: PropTypes.element.isRequired,
    esUrl: PropTypes.string.isRequired,
    version: PropTypes.string.isRequired,
    defaultFilters: PropTypes.shape({
      count_by: PropTypes.oneOf(countByValues),
      pub_status: PropTypes.arrayOf(PropTypes.string),
      alpha_threshold: PropTypes.number,
      pools: PropTypes.arrayOf(PropTypes.string),
      venn_pools: PropTypes.arrayOf(PropTypes.string),
      venn_pools_shorten: PropTypes.object
    }),
    hiddenFilters: PropTypes.arrayOf(PropTypes.oneOf([
      "techs", "pools",
      "3gpp.spec", "3gpp.spec_series", "3gpp.2g_declaration_date", "3gpp.3g_declaration_date", "3gpp.4g_declaration_date", "3gpp.5g_declaration_date"
    ]))
  };

  static defaultProps = {
    defaultFilters: {},
    hiddenFilters: ["techs", "3gpp.spec", "3gpp.spec_series", "3gpp.2g_declaration_date", "3gpp.3g_declaration_date", "3gpp.4g_declaration_date", "3gpp.5g_declaration_date"]
  };

  state = {
    activeSampleTab: "list",
    fieldDate: "priority_date",
    fieldCategory: "assignee_parent",
    fieldDisplaySize: 20
  };

  render() {
    const { activeSampleTab } = this.state;
    const { accessLevel, title, esUrl, version, defaultFilters } = this.props;
    const hiddenFilters = this.props.hiddenFilters.concat(["declared_as_sep"]);
    const query = keepOnlyLandscapeQueryStrings(this.props.location.query);
    const view = countBy2View(query.count_by);
    const alphaThreshold = query.alpha_threshold && isNumber(parseInt(query.alpha_threshold)) ? parseInt(query.alpha_threshold) : defaultFilters.alpha_threshold;
    const filterBase = this._getFilter();
    const filter = this._getSubsetFilter(filterBase);
    const filterPopulation = this._getPopulationFilter(filterBase);
    const fieldAggOptions = [
      { value: "assignee_parent", label: "Parent Company" },
      { value: "assignee_current", label: "Current Assignee" },
      { value: "assignee_original", label: "Original Assignee" },
      { value: "techs", label: "Techs" },
      { value: "inventors", label: "Inventor" },
      { value: "cpc_codes", label: "CPC Code" }
    ].filter(opt => hiddenFilters.indexOf(opt.value) < 0);
    const fieldAggDrilldownOptions = [
      { value: "", label: "None" },
      { value: "publication_type", label: "Publication Type" },
      { value: "country", label: "Publication Country" },
      { value: "declared_as_sep", label: "Declared As SEP" },
      { value: "spec_series", label: "Specification Series" }
    ].filter(opt => hiddenFilters.indexOf(opt.value) < 0);
    const dateHistogramDrilldownOptions = [
      { value: "", label: "None" },
      { value: "publication_type", label: "Publication Type" },
      { value: "declared_as_sep", label: "Declared As SEP" }
    ].filter(opt => hiddenFilters.indexOf(opt.value) < 0);
    const LANDSCAPE_QS = ["count_by", "pub_status", "techs", "alpha_threshold", "country_pop", "declared_as_sep"];
    return (
      <div style={styles.body}>
        {accessLevel === "full" ? null : accessLevel === "trial" ? (
          <Alert
            type="info"
            showIcon={true}
            message="You are using a trial version."
            description="Patent List and some search filters are disabled. Please contact us if you would like to upgrade your account."
            style={{ marginBottom: 16 }}
          />
        ) : accessLevel === "guest" ? (
          <CallToUpgrade style={{ marginBottom: 16 }} />
        ) : (
          <CallToLogin style={{ marginBottom: 16 }} />
        )}
        <Layout>
          <Sider width={260} breakpoint="lg" collapsedWidth="0" style={styles.sider}>
            <div style={{ width: "100%" }}>{title}</div>
            <LandscapeToolbar esUrl={esUrl} version={version} defaultFilters={defaultFilters} hiddenFilters={hiddenFilters} accessLevel={accessLevel} />
          </Sider>
          <Content>
            <AppliedFilters
              fieldSelector={keepOnlyLandscapeQueryStrings}
              fieldMapper={mapLandscapeQueryStringToName}
              omitQueryString={[
                "pub_number",
                "assignee_current",
                "assignee_original",
                "assignee_parent",
                "inventors",
                "country",
                "family_id",
                "extended_family_id",
                "cpc_codes",
                "ipc_codes",
                "uspc_codes",
                "application_date",
                "priority_date",
                "publication_date",
                "expiration_date",
                "pools",
                "full_text",
                "full_text_fields",
                "not"
              ]}
              defaultQuery={{ ...omit(query, LANDSCAPE_QS), ...pick(defaultFilters, LANDSCAPE_QS) }}
              title="Applied Landscape Settings"
              nonClosable={["alpha_threshold", "count_by"]}
              clearButtonText="Reset"
            />
            <AppliedFilters
              fieldSelector={keepOnlyLandscapeQueryStrings}
              fieldMapper={mapLandscapeQueryStringToName}
              defaultQuery={{ ...pick(query, LANDSCAPE_QS), ...omit(defaultFilters, LANDSCAPE_QS) }}
              title="Applied Subset Filters"
              nonClosable={LANDSCAPE_QS}
              clearButtonText="Reset"
              tagColor="blue"
            />
            <Row gutter={8}>
              <Col xl={8} xs={24}>
                <Row gutter={8}>
                  <Col xl={24} sm={12} xs={24}>
                    <Card style={styles.verticalGutterSmall}>
                      <CountHeader mode="population" view={view} esUrl={esUrl} version={version} filter={omit(filter, "alpha_score")} filterPopulation={omit(filterPopulation, "alpha_score")} />
                    </Card>
                  </Col>
                  <Col xl={24} sm={12} xs={24}>
                    <Card style={styles.verticalGutterSmall}>
                      <CountHeader mode="sample" view={view} esUrl={esUrl} version={version} filter={filter} filterPopulation={filterPopulation} />
                    </Card>
                  </Col>
                </Row>
              </Col>
              <Col xl={16} xs={24}>
                <Card style={styles.verticalGutterSmall} bodyStyle={{ padding: 16 }}>
                  <ScoreHistogram
                    view={view}
                    esUrl={esUrl}
                    version={version}
                    alphaBaseline={defaultFilters.alpha_threshold}
                    alphaThreshold={alphaThreshold || 0}
                    filter={omit(filter, ["alpha_score"])}
                    filterPopulation={omit(filterPopulation, "alpha_score")}
                    onSliderChange={this._onAlphaScoreChange}
                  />
                </Card>
              </Col>
            </Row>
            {accessLevel === "full" || accessLevel === "trial" ? (
              <>
                <Tabs size="small" activeKey={activeSampleTab} onChange={val => this.setState({ activeSampleTab: val })} tabBarStyle={{ marginBottom: 0 }} style={styles.verticalGutter}>
                  <TabPane tab="Subset Patents" key="list" disabled={accessLevel !== "full"} />
                  <TabPane tab="Subset Analytics" key="analytics" />
                  <TabPane tab="Citation Analysis" key="citations" />
                </Tabs>

                {activeSampleTab === "list" && <AssetList esUrl={esUrl} version={version} filter={filter} key={JSON.stringify(filter)} />}
                {activeSampleTab === "analytics" && (
                  <>
                    <Card bodyStyle={styles.chartCard}>
                      <Radio.Group value={this.state.fieldCategory} onChange={e => this.setState({ fieldCategory: e.target.value })}>
                        {fieldAggOptions.map((opt, i) => (
                          <Radio.Button value={opt.value} key={i}>
                            {opt.label}
                          </Radio.Button>
                        ))}
                      </Radio.Group>
                      <Divider type="vertical" />
                      <Select
                        value={this.state.fieldDrilldownCategory}
                        placeholder="Drill-Down By"
                        onChange={val => this.setState({ fieldDrilldownCategory: val })}
                        getPopupContainer={trigger => trigger.parentNode}
                        style={{ width: 160 }}
                      >
                        {fieldAggDrilldownOptions.map((opt, i) => (
                          <Option value={opt.value} key={i}>
                            {opt.label}
                          </Option>
                        ))}
                      </Select>
                      <Divider type="vertical" />
                      <Select
                        value={this.state.fieldDisplaySize}
                        placeholder="Display Size"
                        onChange={val => this.setState({ fieldDisplaySize: val })}
                        getPopupContainer={trigger => trigger.parentNode}
                        style={{ width: 130 }}
                      >
                        <Option value={20}>{"Show Top 20"}</Option>
                        <Option value={50}>{"Show Top 50"}</Option>
                        <Option value={100}>{"Show Top 100"}</Option>
                      </Select>
                      <FieldAggregation
                        view={view}
                        field={this.state.fieldCategory}
                        field2={this.state.fieldDrilldownCategory}
                        size={this.state.fieldDisplaySize}
                        esUrl={esUrl}
                        version={version}
                        filter={filter}
                        onSelectBar={v => this.setState({ activeSampleTab: "list" }, () => this._onMergeQueryString(v))}
                      />
                    </Card>
                    <Card style={styles.verticalGutterSmall} bodyStyle={styles.chartCard}>
                      <Radio.Group value={this.state.fieldDate} onChange={e => this.setState({ fieldDate: e.target.value })}>
                        <Radio.Button value="priority_date">{"Priority Date"}</Radio.Button>
                        <Radio.Button value="application_date">{"Filing Date"}</Radio.Button>
                        <Radio.Button value="publication_date">{"Publication Date"}</Radio.Button>
                        <Radio.Button value="expiration_date">{"Expiration Date"}</Radio.Button>
                      </Radio.Group>
                      <Divider type="vertical" />
                      <Select
                        value={this.state.fieldDrilldownDate}
                        placeholder="Drill-Down By"
                        onChange={val => this.setState({ fieldDrilldownDate: val })}
                        getPopupContainer={trigger => trigger.parentNode}
                        style={{ width: 180 }}
                      >
                        {dateHistogramDrilldownOptions.map((opt, i) => (
                          <Option value={opt.value} key={i}>
                            {opt.label}
                          </Option>
                        ))}
                      </Select>
                      <DateHistogram view={view} field={this.state.fieldDate} field2={this.state.fieldDrilldownDate} esUrl={esUrl} version={version} filter={filter} />
                    </Card>
                    <Card style={styles.verticalGutterSmall} bodyStyle={styles.chartCard}>
                      <VennMultiSelect
                        version={version}
                        esUrl={esUrl}
                        defaultSets={defaultFilters.venn_pools}
                        onChange={val => this.setState({ vennCollections: val })}
                        value={this.state.vennCollections}
                      />
                      <VennDiagram
                        size={this.state.fieldDisplaySize}
                        defaultSets={defaultFilters.venn_pools}
                        filter={filter}
                        groups={this.state.vennCollections}
                        version={version}
                        esUrl={esUrl}
                        shortenLabels={defaultFilters.venn_pools_shorten}
                      />
                    </Card>
                  </>
                )}
                {activeSampleTab === "citations" && (
                  <Card>
                    <CitationAnalysis standard={"hevc"} esUrl={esUrl} version={version} filter={filter} />
                  </Card>
                )}
              </>
            ) : accessLevel === "guest" ? (
              <CallToUpgrade style={{ marginBottom: 16 }} />
            ) : (
              <CallToLogin style={{ marginBottom: 16 }} />
            )}
          </Content>
        </Layout>
      </div>
    );
  }

  _getFilter = () => {
    const query = keepOnlyLandscapeQueryStrings(this.props.location.query);
    const { defaultFilters } = this.props;
    return omitBy(
      {
        alpha_score: `${query.alpha_threshold || defaultFilters.alpha_threshold}--`,
        publication_number: toSplittedNumbers(query.pub_number),
        techs: query.techs,
        publication_type: query.count_by && query.count_by.endsWith("grant") ? "G" : query.count_by === "app" ? "A" : undefined,
        pools: query.pools ? (Array.isArray(query.pools) ? query.pools.map(p => `'${p}'`) : [query.pools].map(p => `'${p}'`)) : undefined, // use wrapping quotes to trigger exact match, which differentiate Velos and VelosML
        country_pop: query.country_pop,
        declared_as_sep: query.declared_as_sep,
        family_id: toSplittedNumbers(query.family_id),
        extended_family_id: toSplittedNumbers(query.extended_family_id),
        publication_status: query.pub_status,
        assignee_current: query.assignee_current,
        assignee_original: query.assignee_original,
        assignee_parent: query.assignee_parent,
        inventors: query.inventors,
        country: query.country,
        cpc_codes: query.cpc_codes,
        ipc_codes: query.ipc_codes,
        uspc_codes: query.uspc_codes,
        application_date: query.application_date,
        priority_date: query.priority_date,
        publication_date: query.publication_date,
        expiration_date: query.expiration_date,
        // logical operator
        not: query.not,
        // full text
        full_text: query.full_text,
        full_text_fields: query.full_text_fields,
        // specification related, currently only for 3GPP
        "3gpp.spec": query["3gpp.spec"],
        "3gpp.spec_series": query["3gpp.spec_series"],
        "3gpp.2g_declaration_date": query["3gpp.2g_declaration_date"],
        "3gpp.3g_declaration_date": query["3gpp.3g_declaration_date"],
        "3gpp.4g_declaration_date": query["3gpp.4g_declaration_date"],
        "3gpp.5g_declaration_date": query["3gpp.5g_declaration_date"],
      },
      isEmpty
    );
  };

  _getSubsetFilter = filter => {
    let f = omit(filter, ["country_pop", "country"]);
    f.country = handleCountryFilter(filter.country, filter.country_pop);
    return f;
  };

  _getPopulationFilter = filter => {
    let f = pick(filter, ["alpha_score", "publication_type", "techs", "publication_status", "declared_as_sep"]);
    f.country = filter.country_pop;
    return f;
  };

  _onMergeQueryString = qsObj => {
    this.props.router.push({
      pathname: this.props.location.pathname,
      query: {
        ...this.props.location.query,
        ...qsObj
      }
    });
  };

  _onAlphaScoreChange = val => {
    this._onMergeQueryString({ alpha_threshold: val });
  };
}

export default withRouter(Landscape);
